import { Collapse } from 'antd';
import { ReactNode, useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { SectionSubtitle } from '@core/components';

import './card-item.scss';

const StepInfo = ({ stepInfo }: { stepInfo?: string }): JSX.Element | null =>
    stepInfo ? <span className="step-counter"> ({stepInfo}) </span> : null;

interface CollapsibleArrowProps {
    collapsed?: boolean;
    disabled?: boolean;
}

const CollapsibleArrow = ({ collapsed, disabled }: CollapsibleArrowProps): JSX.Element =>
    collapsed || disabled ? <RightOutlined className="arrow" /> : <RightOutlined className="arrow" rotate={90} />;

interface HeaderProps extends DisabledHeaderProps {
    collapsed?: boolean;
    disabled?: boolean;
}

const Header = ({ collapsed, disabled, title, stepInfo }: HeaderProps): JSX.Element => (
    <>
        <CollapsibleArrow collapsed={collapsed} disabled={disabled} />
        <span className="title">
            {title} <StepInfo stepInfo={stepInfo} />
        </span>
    </>
);

interface DisabledHeaderProps {
    title: string;
    stepInfo?: string;
}

const DisabledHeader = ({ title, stepInfo }: DisabledHeaderProps): JSX.Element => (
    <Header disabled title={title} stepInfo={stepInfo} />
);

interface CardItemProps {
    children?: ReactNode;
    collapsed?: boolean;
    description?: string;
    disabled?: boolean;
    extra?: ReactNode;
    id: number;
    stepInfo?: string;
    title: string;
    emphasis?: boolean;
    className?: string;
}

const { Panel } = Collapse;

export const CardItem = ({
    className = '',
    title,
    id,
    stepInfo,
    description,
    extra,
    children,
    disabled,
    collapsed,
    emphasis = false,
}: CardItemProps): JSX.Element => {
    const [_isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const isCollapsed = collapsed ?? _isCollapsed;

    const onChange = () => {
        setIsCollapsed(!isCollapsed);
    };

    if (disabled) {
        return (
            <Collapse className={`${className} container-card--disabled`} defaultActiveKey={['1']} destroyInactivePanel>
                <Panel
                    showArrow={false}
                    header={<DisabledHeader title={title} stepInfo={stepInfo} />}
                    key="2"
                    extra={extra}
                    collapsible="disabled"
                />
            </Collapse>
        );
    }

    if (collapsed) {
        return (
            <Collapse
                className={`${className} container-card`}
                defaultActiveKey={['1']}
                destroyInactivePanel
                onChange={onChange}
            >
                <Panel
                    showArrow={false}
                    header={<Header title={title} collapsed={isCollapsed} />}
                    key="3"
                    extra={extra}
                    collapsible="header"
                >
                    <SectionSubtitle description={description} showLine={false} emphasis={emphasis} />
                </Panel>
            </Collapse>
        );
    }

    return (
        <Collapse
            className={`${className} container-card modal-mount-${id}`}
            defaultActiveKey={['1']}
            onChange={onChange}
        >
            <Panel
                showArrow={false}
                header={<Header title={title} stepInfo={stepInfo} collapsed={isCollapsed} />}
                key="1"
                extra={extra}
                collapsible={'header'}
            >
                <SectionSubtitle description={description} showLine={false} emphasis={emphasis} />
                {children}
            </Panel>
        </Collapse>
    );
};
